// src/pages/ArticleDetailPage.tsx
import { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from '../components/Router';
import { Calendar, Clock, ArrowLeft, Eye, X, Share2, Bookmark, ChevronLeft, ChevronRight, Loader2, Image as ImageIcon, Facebook, Twitter, Linkedin } from 'lucide-react';
import { trackPageView } from '../utils/analytics';
import { supabase } from '../lib/supabase';
import { slugify } from '../utils/slugify';

interface ArticleImage {
  id: string;
  article_id: string;
  image_url: string;
  alt_text?: string;
  display_order: number;
}

interface ActualiteItem {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  publish_date: string;
  read_time: string;
  category: string;
  image_url: string;
  featured: boolean;
  views?: number;
  likes?: number;
  comments?: number;
  images?: ArticleImage[];
  slug?: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  'Actualités': 'bg-blue-50 text-blue-700',
  'Réglementation': 'bg-purple-50 text-purple-700',
  'Marché': 'bg-green-50 text-green-700',
  'Conseils': 'bg-yellow-50 text-yellow-700',
  'Analyse': 'bg-red-50 text-red-700'
};

const cleanArticleHtml = (htmlContent: string | null | undefined, heroImageUrl: string | null) => {
  if (!htmlContent) return '';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const h1s = doc.querySelectorAll('h1');
    h1s.forEach(el => {
      if (el.textContent && el.textContent.trim().length > 0) el.remove();
    });
    const firstImg = doc.querySelector('img');
    if (firstImg && heroImageUrl) {
      const imgSrc = firstImg.src || firstImg.getAttribute('src') || '';
      if (imgSrc.includes(heroImageUrl) || heroImageUrl.includes(imgSrc)) firstImg.remove();
    }
    return doc.body.innerHTML;
  } catch (e) {
    return htmlContent;
  }
};

// --- ROBUST SHARE BUTTON (Standard Links) ---
const ShareButton = ({ articleUrl }: { articleUrl: string }) => {
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  
  // Use encoded URL
  const encodedUrl = encodeURIComponent(articleUrl);

  return (
    <div className="relative z-50">
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsShareMenuOpen(!isShareMenuOpen);
        }}
        className="p-2 rounded-full bg-gray-100 text-gray-600 hover:text-blue-600 hover:bg-gray-200 transition-colors"
        aria-label="Partager"
      >
        <Share2 className="h-4 w-4" />
      </button>

      {isShareMenuOpen && (
        <div className="absolute top-full right-0 mt-2 p-2 bg-white/95 backdrop-blur-xl rounded-2xl border border-gray-200 shadow-xl flex gap-2 animate-in fade-in zoom-in duration-200 w-max" onClick={(e) => e.stopPropagation()}>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors flex items-center justify-center"
            title="Facebook"
          >
            <Facebook className="w-5 h-5" />
          </a>
          <a
            href={`https://twitter.com/intent/tweet?url=${encodedUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-black hover:bg-gray-100 rounded-full transition-colors flex items-center justify-center"
            title="X (Twitter)"
          >
            <Twitter className="w-5 h-5" />
          </a>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-blue-700 hover:bg-blue-50 rounded-full transition-colors flex items-center justify-center"
            title="LinkedIn"
          >
            <Linkedin className="w-5 h-5" />
          </a>
        </div>
      )}
    </div>
  );
};

// ... (ImageModal remains the same) ...
function ImageModal({ images, initialIndex, onClose }: { images: ArticleImage[]; initialIndex: number; onClose: () => void; }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const goToPrevious = () => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  const goToNext = () => setCurrentIndex((prev) => (prev + 1) % images.length);

  return (
    <div className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative max-w-6xl max-h-full w-full h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <div className="text-white text-sm font-medium">{currentIndex + 1} / {images.length}</div>
          <button onClick={onClose} className="text-white hover:text-gray-300 p-2 rounded-full hover:bg-white/10 transition-colors">
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="relative flex-1 flex items-center justify-center">
          <img src={images[currentIndex].image_url} alt={images[currentIndex].alt_text || 'Gallery image'} className="max-w-full max-h-full object-contain" />
          {images.length > 1 && (
            <>
              <button onClick={goToPrevious} className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 backdrop-blur-md text-white rounded-full p-2.5 hover:bg-black/70 transition-colors"><ChevronLeft className="h-6 w-6" /></button>
              <button onClick={goToNext} className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 backdrop-blur-md text-white rounded-full p-2.5 hover:bg-black/70 transition-colors"><ChevronRight className="h-6 w-6" /></button>
            </>
          )}
        </div>
        {images[currentIndex].alt_text && <div className="text-white text-center mt-4 text-sm">{images[currentIndex].alt_text}</div>}
      </div>
    </div>
  );
}

export function ArticleDetailPage() {
  const { id, slug } = useParams<{ id?: string; slug?: string }>();
  const [article, setArticle] = useState<ActualiteItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const [galleryImages, setGalleryImages] = useState<ArticleImage[]>([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalInitialIndex, setModalInitialIndex] = useState(0);
  const [savedArticle, setSavedArticle] = useState(false);
  const [relatedArticles, setRelatedArticles] = useState<ActualiteItem[]>([]);
  const galleryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const identifier = slug || id;
    if (!identifier) return;
    trackPageView(`/actualite/${identifier}`);
    
    const fetchArticle = async () => {
      try {
        setIsLoading(true);
        setError(null);
        let query = supabase.from('actualite').select('*');
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(identifier);
        
        if (isUUID) query = query.eq('id', identifier);
        else query = query.eq('slug', identifier);
        
        let { data, error: fetchError } = await query.maybeSingle();

        if (!data && !isUUID) {
          const { data: allArticles } = await supabase.from('actualite').select('*');
          if (allArticles) {
            const found = allArticles.find(a => slugify(a.title) === identifier || (a.slug && slugify(a.slug) === identifier));
            if (found) { data = found; fetchError = null; }
          }
        }

        if (fetchError) throw fetchError;
        if (!data) throw new Error('Article non trouvé');
        
        const { data: related } = await supabase.from('actualite').select('*').eq('category', data.category).neq('id', data.id).limit(3);
        setArticle(data);
        setRelatedArticles(related || []);
        await supabase.rpc('increment_article_views', { article_id: data.id });
        if (data.id) loadGalleryImages(data.id);
        
      } catch (err: any) {
        console.error('Erreur chargement article:', err);
        setError('Impossible de charger l\'article.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchArticle();
  }, [id, slug]);

  const loadGalleryImages = async (articleId: string) => {
    try {
      setGalleryLoading(true);
      const { data: images } = await supabase.from('article_images').select('*').eq('article_id', articleId).order('display_order', { ascending: true });
      if (images && images.length > 0) {
        setGalleryImages(images);
        setLoadedImages(new Set([0]));
      }
    } catch (error) { console.error('Error loading gallery:', error); } 
    finally { setGalleryLoading(false); }
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .article-content { line-height: 1.6; }
      .article-content table { width: 100%; border-collapse: collapse; margin: 1em 0; border: 1px solid #e5e7eb; }
      .article-content th, .article-content td { border: 1px solid #e5e7eb; padding: 8px 12px; text-align: left; }
      .article-content th { background-color: #f9fafb; font-weight: bold; }
      .article-content h1 { font-size: 2rem; font-weight: 700; margin-top: 1.5rem; margin-bottom: 1rem; color: #111827; line-height: 1.2; }
      .article-content h2 { font-size: 1.5rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 1rem; color: #111827; line-height: 1.3; }
      .article-content h3 { font-size: 1.25rem; font-weight: 600; margin-top: 1.25rem; margin-bottom: 0.75rem; color: #111827; line-height: 1.4; }
      .article-content h4 { font-size: 1.125rem; font-weight: 600; margin-top: 1.25rem; margin-bottom: 0.75rem; color: #111827; }
      .article-content h5, .article-content h6 { font-size: 1rem; font-weight: 600; margin-top: 1rem; margin-bottom: 0.5rem; color: #111827; }
      .article-content ul { list-style-type: disc; padding-left: 1.5rem; margin-bottom: 1rem; }
      .article-content ol { list-style-type: decimal; padding-left: 1.5rem; margin-bottom: 1rem; }
      .article-content li { margin-bottom: 0.5rem; }
      .article-content p { margin-bottom: 1rem; line-height: 1.7; }
      .article-content a { color: #D5462E; text-decoration: underline; }
      .article-content a:hover { color: #b83a24; }
      .article-content blockquote { border-left: 4px solid #D5462E; margin: 1em 0; padding: 0.5em 1em; color: #6b7280; font-style: italic; background-color: #f9fafb; }
      .article-content code { background-color: #f3f4f6; padding: 0.25rem 0.5rem; border-radius: 0.25rem; font-family: monospace; font-size: 0.875em; }
      .article-content img { max-width: 100%; height: auto; display: block; margin: 1rem auto; border-radius: 0.375rem; }
      .article-content .custom-table-container { overflow-x: auto; margin: 1em 0; }
    `;
    document.head.appendChild(style);
    return () => { if (document.head.contains(style)) document.head.removeChild(style); };
  }, []);

  const nextGalleryImage = () => {
    if (galleryImages.length <= 1) return;
    const nextIndex = (currentGalleryIndex + 1) % galleryImages.length;
    setCurrentGalleryIndex(nextIndex);
    if (!loadedImages.has(nextIndex)) setLoadedImages(prev => new Set(prev).add(nextIndex));
  };

  const prevGalleryImage = () => {
    if (galleryImages.length <= 1) return;
    const prevIndex = (currentGalleryIndex - 1 + galleryImages.length) % galleryImages.length;
    setCurrentGalleryIndex(prevIndex);
    if (!loadedImages.has(prevIndex)) setLoadedImages(prev => new Set(prev).add(prevIndex));
  };

  const openImageModal = (index: number) => { setModalInitialIndex(index); setIsModalOpen(true); };
  const closeModal = () => setIsModalOpen(false);
  const toggleSavedArticle = () => setSavedArticle(prev => !prev);

  if (isLoading) return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="animate-spin h-10 w-10 text-[#D5462E]" /></div>;
  if (error || !article) return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="text-center bg-red-50 backdrop-blur-xl p-8 rounded-3xl border border-red-200 max-w-md">
        <h3 className="text-lg font-semibold text-red-900 mb-2">Erreur</h3>
        <p className="text-sm text-red-700 mb-4">{error || 'Article non trouvé'}</p>
        <Link to="/actualite" className="text-red-600 hover:underline font-medium">Retour</Link>
      </div>
    </div>
  );

  const finalContentHtml = cleanArticleHtml(article.content, article.image_url);

  return (
    <div className="min-h-screen bg-white">
      {/* Client-side Helmet as a fallback for internal navigation titles */}
      <Helmet>
        <title>{article.title} | Argus Tunisie</title>
        <meta property="og:title" content={article.title} />
        <meta property="og:image" content={article.image_url} />
      </Helmet>

      <div className="bg-white/90 backdrop-blur-xl border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <Link to="/actualite" className="inline-flex items-center text-gray-600 hover:text-[#D5462E] transition-colors text-sm font-medium">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Retour aux actualités
            </Link>
            <div className="flex items-center space-x-2">
              <button onClick={toggleSavedArticle} className="p-2 rounded-full bg-gray-100 text-gray-600 hover:text-[#D5462E] hover:bg-gray-200 transition-colors">
                <Bookmark className={`h-4 w-4 ${savedArticle ? 'fill-current text-[#D5462E]' : ''}`} />
              </button>
              <ShareButton articleUrl={window.location.href} title={article.title} />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <article className="bg-white/90 backdrop-blur-xl rounded-3xl overflow-hidden mb-6 border border-gray-200">
          <div className="relative h-64 sm:h-80 md:h-96 bg-gray-100">
            <img src={article.image_url || '/placeholder-image.jpg'} alt={article.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = '/placeholder-image.jpg'; }} />
          </div>
          <div className="p-8">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${CATEGORY_COLORS[article.category] || 'bg-gray-50 text-gray-800'}`}>{article.category}</span>
              <span className="text-gray-500 flex items-center text-xs"><Clock className="h-3 w-3 mr-1" />{article.read_time}</span>
              {article.views && <span className="text-gray-500 flex items-center text-xs"><Eye className="h-3 w-3 mr-1" />{article.views}</span>}
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-4 leading-tight">{article.title}</h1>
            <div className="flex items-center text-sm text-gray-500 mb-6 pb-6 border-b border-gray-200">
              <Calendar className="h-4 w-4 mr-1" />
              {new Date(article.publish_date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
            {article.excerpt && (
              <div className="mb-8 py-2">
                <p className="text-lg sm:text-xl text-gray-700 italic font-serif leading-relaxed border-l-4 border-[#D5462E] pl-4 bg-gray-50 py-2 rounded-r-lg">{article.excerpt}</p>
              </div>
            )}
            <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed mb-6 article-content" dangerouslySetInnerHTML={{ __html: finalContentHtml }} />
          </div>
        </article>

        {galleryImages.length > 0 && (
          <div ref={galleryRef} className="bg-white/90 backdrop-blur-xl rounded-3xl overflow-hidden mb-6 border border-gray-200">
            <div className="p-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center"><ImageIcon className="h-5 w-5 mr-2 text-[#D5462E]" />Galerie ({galleryImages.length})</h2>
              <div className="relative h-96 sm:h-[28rem] md:h-[32rem] bg-gray-100 rounded-3xl overflow-hidden">
                 <img src={galleryImages[currentGalleryIndex].image_url} alt={galleryImages[currentGalleryIndex].alt_text || 'Gallery image'} className="w-full h-full object-cover cursor-pointer" onClick={() => openImageModal(currentGalleryIndex)} />
                  {galleryImages.length > 1 && (
                    <>
                      <button onClick={(e) => { e.stopPropagation(); prevGalleryImage(); }} className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/40 backdrop-blur-md text-white rounded-full p-2.5 hover:bg-black/60 transition-colors"><ChevronLeft className="h-5 w-5" /></button>
                      <button onClick={(e) => { e.stopPropagation(); nextGalleryImage(); }} className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/40 backdrop-blur-md text-white rounded-full p-2.5 hover:bg-black/60 transition-colors"><ChevronRight className="h-5 w-5" /></button>
                    </>
                  )}
              </div>
              {galleryImages.length > 1 && (
                <div className="flex justify-center mt-4 space-x-2">
                  {galleryImages.map((_, index) => (
                    <button key={index} onClick={() => setCurrentGalleryIndex(index)} className={`w-2 h-2 rounded-full transition-colors ${index === currentGalleryIndex ? 'bg-[#D5462E]' : 'bg-gray-300 hover:bg-gray-400'}`} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {relatedArticles.length > 0 && (
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-8 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Articles similaires</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {relatedArticles.map((relatedArticle) => (
                <Link key={relatedArticle.id} to={`/actualite/${slugify(relatedArticle.title)}`} className="bg-white rounded-2xl overflow-hidden hover:bg-gray-50 transition-all border border-gray-200 block">
                  <div className="h-32 overflow-hidden">
                    <img src={relatedArticle.image_url || '/placeholder-image.jpg'} alt={relatedArticle.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" onError={(e) => { e.currentTarget.src = '/placeholder-image.jpg'; }} />
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 hover:text-[#D5462E] transition-colors">{relatedArticle.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
      {isModalOpen && <ImageModal images={galleryImages} initialIndex={modalInitialIndex} onClose={closeModal} />}
    </div>
  );
}